import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const faqs = [
  {
    question: "What does co-hosting include?",
    answer:
      "Our co-hosting service covers everything needed to run your listing professionally: guest communication (24/7 inquiry responses, booking confirmations, check-in instructions), booking calendar management, review management, operational coordination with cleaners and maintenance teams, and monthly performance reporting. You retain ownership of your listing while we handle the day-to-day operations.",
  },
  {
    question: "What does your listing optimization process look like?",
    answer:
      "We start with a comprehensive audit of your current listing — photos, title, description, pricing, and amenities. We then rewrite your listing copy with SEO-optimized titles and compelling descriptions, advise on photography improvements, set up a dynamic pricing strategy, and optimize your listing across all platforms. Most clients see measurable improvements in visibility and bookings within the first 30 days.",
  },
  {
    question: "Do you work with new hosts who don't have a listing yet?",
    answer:
      "Absolutely. We love working with new hosts. We can guide you through property selection, furnishing and interior design, professional staging, listing creation, and full setup on Airbnb, VRBO, and Booking.com. Our STR Business Launch coaching program is specifically designed for new hosts and investors starting from scratch.",
  },
  {
    question: "What coaching programs do you offer?",
    answer:
      "We offer three coaching programs: (1) Listing Optimization — for existing hosts who want to improve performance, including 2 one-on-one sessions ($497). (2) STR Business Launch — a complete program for new hosts or investors, including 6 sessions and 90 days of email support ($1,497). (3) Performance Audit — a deep-dive analysis with a detailed action plan and 1 strategy session ($297). All programs are tailored to your specific property and market.",
  },
  {
    question: "How is your pricing structured — commission or flat fee?",
    answer:
      "Our management and co-hosting services are typically structured as a percentage of monthly revenue (commission-based), which aligns our incentives with yours — we earn more when you earn more. Coaching programs are one-time flat fees. Specific rates are discussed during your discovery call based on your property type, location, and the scope of services required.",
  },
  {
    question: "Which platforms do you support?",
    answer:
      "We primarily work with Airbnb, VRBO, and Booking.com. We can manage your listings across all three platforms simultaneously to maximize your property's visibility and booking potential. We also advise on direct booking strategies to reduce platform dependency over time.",
  },
  {
    question: "How long does it take to launch a new listing?",
    answer:
      "For a property that's already furnished and ready, we can typically have a fully optimized listing live within 5–10 business days. If the property needs furnishing, staging, or photography, the timeline is 3–6 weeks depending on the scope of work. We'll give you a clear timeline during your onboarding call.",
  },
  {
    question: "What does the onboarding process look like?",
    answer:
      "After you submit your Get Started application, we schedule a discovery call to understand your goals and property. We then conduct a property audit, develop a custom strategy, and present our recommendations. Once you're onboard, we handle setup, optimization, and launch. You'll have a dedicated point of contact throughout the process and receive regular updates.",
  },
  {
    question: "How do you handle guest issues or emergencies?",
    answer:
      "We have systems in place for rapid response to guest concerns. Our team monitors messages around the clock and responds to urgent issues promptly. For maintenance emergencies, we coordinate with trusted local vendors. Our Lagos experience taught us to build contingency plans for every scenario — from power outages to last-minute maintenance — so guests always receive a 5-star experience regardless of circumstances.",
  },
  {
    question: "How is listing performance tracked and reported?",
    answer:
      "We provide monthly performance reports covering key metrics: occupancy rate, average daily rate (ADR), revenue per available night (RevPAN), review scores, and booking trends. We also track your listing's search ranking and visibility. Reports include actionable insights and recommendations for the following month. You'll always know exactly how your property is performing.",
  },
  {
    question: "Why should I trust Upgraded Version over other management companies?",
    answer:
      "Our credibility comes from where we started. We built our systems in Lagos, Nigeria — one of the most operationally challenging cities for short-term rentals due to infrastructure issues. Despite those conditions, we maintained a 4.95 Airbnb rating. If our systems work in Lagos, they work anywhere. Today we manage 15+ listings and have helped many hosts launch and improve their STR businesses.",
  },
  {
    question: "Can you help me if my property is outside Nigeria?",
    answer:
      "Yes. While we're based in Lagos, our management and coaching services are available to hosts globally. Our virtual assistance and remote co-hosting services are location-independent. For coaching, all sessions are conducted online and our strategies apply to any market. We've worked with hosts across multiple countries and continents.",
  },
];

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-block bg-amber-100 text-amber-900 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            FAQ
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Frequently Asked<br />
            <span className="text-amber-900">Questions</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Everything you need to know about our services, coaching programs, and how we work with hosts.
          </p>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`border rounded-2xl overflow-hidden transition-all duration-200 ${
                  openIndex === index
                    ? 'border-amber-400 shadow-md'
                    : 'border-gray-200 hover:border-amber-300'
                }`}
              >
                <button
                  onClick={() => toggle(index)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left bg-white cursor-pointer"
                >
                  <span className="text-base font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  <span
                    className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-200 ${
                      openIndex === index
                        ? 'bg-amber-900 text-white rotate-45'
                        : 'bg-amber-100 text-amber-900'
                    }`}
                  >
                    <i className="ri-add-line text-lg"></i>
                  </span>
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-6 bg-white">
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-gradient-to-br from-amber-900 to-amber-950">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            We're happy to chat. Reach out directly or start your application and we'll be in touch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-amber-900 rounded-full text-sm font-medium hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
            >
              Contact Us
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/get-started"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-white rounded-full text-sm font-medium hover:bg-white hover:text-amber-900 transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
